import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_NAMES = { starter: "Base", pro: "Pro", institutional: "Institutional" };

export default function PaymentPending() {
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const res = await axios.get(`${API}/payments/my-pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.found) setPayment(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    // Poll every 30s
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const status = payment?.status || "pending";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-28 pb-20 px-4 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full bg-[#111111] border border-[#27272A] rounded-2xl p-8 text-center space-y-5">

          {loading ? (
            <Loader2 className="w-12 h-12 text-[#00E676] animate-spin mx-auto" />
          ) : status === "approved" ? (
            <>
              <div className="w-20 h-20 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-[#00E676]" />
              </div>
              <h1 className="text-2xl font-bold text-[#00E676]">Payment Approved! 🎉</h1>
              <p className="text-[#A1A1AA]">Your <strong className="text-white">{PLAN_NAMES[payment.plan]}</strong> subscription is now active.</p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#00E676] hover:bg-[#00C853] text-black font-semibold"
              >
                Go to Dashboard
              </Button>
            </>
          ) : status === "rejected" ? (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-red-400">Payment Rejected</h1>
              <p className="text-[#A1A1AA]">Your payment could not be verified. Please contact us on WhatsApp for assistance.</p>
              <Button
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="w-full border-[#27272A] hover:bg-[#1C1C1E]"
              >
                Try Again
              </Button>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto">
                <Clock className="w-10 h-10 text-yellow-400 animate-pulse" />
              </div>
              <h1 className="text-2xl font-bold">Awaiting Confirmation</h1>
              <p className="text-[#A1A1AA]">
                We received your payment submission for the{" "}
                <span className="text-white font-semibold">{PLAN_NAMES[payment?.plan] || "selected"}</span> plan.
                The owner will verify your transaction and activate your subscription — usually within a few hours.
              </p>
              {payment?.txHash && (
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-left">
                  <p className="text-xs text-[#52525B] mb-1">Your submitted TxHash</p>
                  <p className="font-mono text-xs text-[#A1A1AA] break-all">{payment.txHash}</p>
                </div>
              )}
              <div className="text-xs text-[#52525B]">Auto-refreshing every 30 seconds…</div>
              <Button
                onClick={fetchStatus}
                variant="outline"
                className="w-full border-[#27272A] hover:bg-[#1C1C1E]"
              >
                Check Status Now
              </Button>
              <a
                href="https://wa.link/og6izz"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-[#00E676] hover:underline mt-2"
              >
                Need help? Contact us on WhatsApp →
              </a>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
